"use client";

import { cn } from "@/lib/utils/utils";
import { pad2 } from "@/lib/blog/format";

import { useBlogReader } from "../BlogReaderProvider";

/**
 * The sticky index column, lg and up.
 *
 * Two things move: the stage numeral rolls like a slot machine as the reader
 * crosses into a new section, and the rail fills to show how far through the
 * run of sections they are. Both are transforms on elements that are already
 * in the layout — nothing here reflows.
 */
const BlogDesktopIndex = () => {
  const { toc, sectionLabel, active, railProgress } = useBlogReader();

  if (toc.length === 0) return null;

  return (
    <aside className="sticky top-28 hidden lg:block">
      {/* The numeral is decorative duplication of the index below it; the
          heading text is what a screen reader should get. */}
      <div
        aria-hidden="true"
        className="relative h-[150px] overflow-hidden"
      >
        {toc.map((item) => {
          const state =
            item.index === active ? "on" : item.index < active ? "up" : "down";
          return (
            <b
              key={item.id}
              className={cn(
                "absolute inset-0 block text-[148px] leading-[150px] font-bold tracking-[-0.06em] text-ink transition-[transform,opacity] duration-[0.75s] ease-[cubic-bezier(.16,1,.3,1)] tabular-nums",
                state === "on" && "translate-y-0 opacity-100",
                state === "up" && "-translate-y-full opacity-0",
                state === "down" && "translate-y-full opacity-0",
              )}
            >
              {pad2(item.index)}
            </b>
          );
        })}
      </div>

      <p className="mt-2.5 text-xs tracking-[0.12em] text-ink-mute uppercase">
        The {sectionLabel.toLowerCase()} list
      </p>

      <nav
        aria-label="On this page"
        className="mt-5 grid grid-cols-[2px_1fr] gap-x-4"
      >
        <div className="rounded-sm bg-rule">
          <div
            className="h-full w-0.5 origin-top rounded-sm bg-ink transition-transform duration-150 ease-linear"
            style={{ transform: `scaleY(${railProgress})` }}
          />
        </div>

        <ol>
          {toc.map((item) => {
            const current = item.index === active;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  aria-current={current ? "true" : undefined}
                  className={cn(
                    "relative flex min-h-11 gap-3.5 py-3 pl-5 text-sm leading-[1.45] transition-[color,transform] duration-300 ease-[cubic-bezier(.22,.61,.36,1)]",
                    current
                      ? "translate-x-1.5 text-ink"
                      : "text-ink-mute hover:text-ink-soft",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute top-3 bottom-3 left-0 w-0.5 origin-top transition-transform duration-[0.45s] ease-[cubic-bezier(.22,.61,.36,1)]",
                      current ? "scale-y-100 bg-primary" : "scale-y-0 bg-ink",
                    )}
                  />
                  <span className="opacity-55 tabular-nums">
                    {pad2(item.index)}
                  </span>
                  <span>{item.title}</span>
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
    </aside>
  );
};

export default BlogDesktopIndex;
